/* eslint-disable camelcase */
// TODO: Uncomment plot edit sections when FE plot edit functionality is working
import { useMutationState } from '@tanstack/react-query';
import {
  Cell,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Row,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  Button,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownItemLink,
  //  DropdownItemLinkProps,
  DropdownItemProps,
  DropdownTrigger,
  Loader,
  Pagination,
  RiArrowRightSLine,
  RiDeleteBinLine,
  //  RiEditLine,
  RiEyeLine,
  RiMore2Line,
  Spinner,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from 'component-library';
import { TextInput } from 'component-library/TextInput/TextInput';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, matchPath, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { MembershipWithOrganizationTypeEnum } from '@/api/rest/resources/types/membership';
import { OrganizationUIStyle } from '@/api/rest/resources/types/organization';
import { Plot, PlotStatusEnum, PlotWithFacts } from '@/api/rest/resources/types/plot';
import { PlotLabelValue } from '@/api/rest/resources/types/plotGroup';
import { WidgetSlotConfiguration } from '@/api/rest/resources/types/widget';
import { withSuspenseBoundary } from '@/components';
import { useSetDetailsPlotGroup } from '@/components/PlotGroups/hooks/useSetPlotGroup';
import { useActivePlotGroupPlotLabel } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { usePlotGroupSortConfiguration } from '@/components/widgets/config';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { UNANALYZABLE_PLOT_STATUS_TYPES } from '@/config/constants';
import { useDebounce } from '@/hooks/useDebounce';
import { useEffectOnce } from '@/hooks/useEffectOnce';
import { useMembershipType } from '@/hooks/useMembershipType';
import { usePathSet } from '@/hooks/usePathSet';
import { useUIStyle } from '@/hooks/useUIStyle';
// import { Logger } from '@/lib/logs/logger';
import { PlotRow } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/components/plot-list/Cells';
import {
  CheckboxCell,
  LabelNameCell,
  NameCell,
} from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plots/components/Cells';
import { paths } from '@/routing';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';

import { CreatePlotGroup } from '../../components/plot-group/CreatePlotGroup';
import { useIsLimitedAccessUser } from '../../hooks/useIsLimitedAccessUser';
import { useIsOrganizationMember } from '../../hooks/useIsOrganizationMember';
import { useOrganizationId } from '../../hooks/useOrganizationId';
import { usePlotLabelNameValues } from '../../hooks/usePlotLabels';
import { usePlotsForOrganization } from '../../hooks/usePlotsForOrganization';
import {
  PAGE_SIZE,
  ProjectTypeAccessorKey,
} from '../../projects/pages/project/pages/sustainableProduction/components/plot-list/constants';
import { DeletePlotWithConfirmation } from '../../projects/pages/project/pages/sustainableProduction/components/plot-list/DeletePlotWithConfirmation';
import { ToggleSortButton } from '../../projects/pages/project/pages/sustainableProduction/components/plot-list/ToggleSortButton';
import { useDestinationPath } from '../hooks/useDestinationPath';
import { MetadataColumn, useGetCustomCells } from '../hooks/useGetCustomCells';
import { SelectAllPlotsCheckbox } from './SelectAllPlotsCheckbox';

export type PlotListDesktopProps = {
  plotFilters?: Record<string, unknown>;
  customColumns?: MetadataColumn[];
  showSort?: boolean;
  showActions?: boolean;
  pageSize?: number;
  showPagination?: boolean;
  organizationIdArg?: string;
  defaultRowSelected?: boolean;
  enableRowSelection?: boolean;
  setSelectedPlotIds?: (ids: string[]) => void;
  selectedPlotIds?: string[];
};

export const PlotList: FC<PlotListDesktopProps> = withSuspenseBoundary(
  (props) => {
    return <PlotListBase {...props} />;
  },
  <div className='bg-white-100 rounded-2xl border border-neutral-black-12'>
    <Loader className='min-h-[300px] relative' />
  </div>,
);

const PlotListBase: FC<PlotListDesktopProps> = ({
  plotFilters,
  customColumns,
  showSort = true,
  showActions = true,
  pageSize = PAGE_SIZE,
  showPagination = true,
  organizationIdArg,
  defaultRowSelected = false,
  enableRowSelection = false,
  setSelectedPlotIds,
  selectedPlotIds,
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const membershipType = useMembershipType();
  const pathSet = usePathSet();
  const organizationId = useOrganizationId();

  const isLimitedAccessUser = useIsLimitedAccessUser();
  const isOrganizationMember = useIsOrganizationMember();
  const plotGroupId = searchParams.get('plotGroupId');

  const defaultColumns = useGetCustomCells();
  const customCells = customColumns ?? defaultColumns;

  const sortWidgetConfiguration = usePlotGroupSortConfiguration();
  const showGroupByOptions = !useUIStyle(OrganizationUIStyle.CONSERVATION);

  const [plotForDeletion, setPlotForDeletion] = useState<Plot | null>(null);
  const [allPlots, setAllPlots] = useState<Plot[] | null>(null); // We use a paginated view. But all plots are required to create plot groups by clickin on the check boxes. So when the user clicks on the table header checkbox, we need to query them all and then replace the table plots by the complete query

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 800);

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const setCurrentPage = (page: number) => {
    setSearchParams({ page: `${page}`, ...(plotGroupId && { plotGroupId }) }, { preventScrollReset: true });
  };
  const organisationId = useOrganizationId();

  const resolvedPlotFilters = useMemo(() => {
    const externalFilters = plotFilters && Object.keys(plotFilters).length > 0 ? plotFilters : {};
    const searchFilters = debouncedSearchQuery ? { query: debouncedSearchQuery } : {};
    return Object.keys(externalFilters).length > 0 || debouncedSearchQuery
      ? { ...externalFilters, ...searchFilters }
      : undefined;
  }, [plotFilters, debouncedSearchQuery]);

  const plotsForOrganizationQuery = usePlotsForOrganization(
    organizationIdArg ?? organisationId,
    resolvedPlotFilters,
    PAGE_SIZE,
    (currentPage - 1) * PAGE_SIZE,
    plotGroupId ?? undefined,
  );
  const { results: plotDetails, asset_column_name, count: totalPlotCount } = plotsForOrganizationQuery.data;

  const plotLabel = useActivePlotGroupPlotLabel();
  const plotLabelValues = usePlotLabelNameValues(plotLabel?.id || null).data?.results ?? null;

  const isRenderingPlotLabels = !!plotLabel?.id;
  const isPlotsPage = !!matchPath(pathSet.plots, pathname);

  const plots = useMemo(() => {
    if (!isNullish(allPlots)) {
      return allPlots;
    }

    const factsReport = (facts: PlotReportFactElement[]) => {
      return facts.reduce<Record<string, PlotReportFactElement>>((accumulator, item) => {
        if (!accumulator[item.name]) {
          accumulator[item.name] = item;
        }
        return accumulator;
      }, {});
    };

    return plotDetails.map((plotDetail: PlotWithFacts) => {
      return { ...plotDetail, facts: factsReport(plotDetail.facts) };
    });
  }, [plotDetails, allPlots]);

  const confirmPlotDelete = (plot: Plot) => {
    setPlotForDeletion(plot);
  };

  const [sorting, setSorting] = useState<SortingState>([]);
  const [internalSelectedPlotIds, setInternalSelectedPlotIds] = useState<string[]>([]);

  const currentSelectedPlotIds = selectedPlotIds ?? internalSelectedPlotIds;

  const updateSelectedPlotIds = useCallback(
    (ids: string[]) => {
      if (selectedPlotIds === undefined) {
        setInternalSelectedPlotIds(ids);
      }

      setSelectedPlotIds?.(ids);
    },
    [selectedPlotIds, setSelectedPlotIds],
  );

  const columns = useMemo<ColumnDef<PlotRow>[]>(
    () => [
      ...(enableRowSelection
        ? [
            {
              id: 'select',
              accessorKey: 'select',
              header: () => (
                <SelectAllPlotsCheckbox
                  totalPlotCount={totalPlotCount}
                  currentSelectedPlotIds={currentSelectedPlotIds}
                  updateSelectedPlotIds={updateSelectedPlotIds}
                  resolvedPlotFilters={resolvedPlotFilters}
                  organizationId={organizationIdArg ?? organisationId}
                  setAllPlots={setAllPlots}
                />
              ),
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              cell: ({ row }: { row: any }) => (
                <CheckboxCell
                  {...{
                    checked: row.getIsSelected(),
                    disabled: !row.getCanSelect(),
                    indeterminate: row.getIsSomeSelected(),
                    onChange: row.getToggleSelectedHandler(),
                  }}
                />
              ),
            },
          ]
        : []),
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <ToggleSortButton column={column} className='-ml-2.5'>
            {asset_column_name ?? t('shared.plots.tableHeader.name')}
          </ToggleSortButton>
        ),
        cell: (context) => {
          const rowPlot = context.row.original;
          if (rowPlot.id !== rowPlot.name) {
            // Grouped plots have the same id and name
            return <NameCell {...context} />;
          }
          const showLabelView = isRenderingPlotLabels || !rowPlot.project_id;
          return showLabelView ? <LabelNameCell {...context} /> : <NameCell {...context} />;
        },
      },
      ...customCells,
      {
        accessorKey: 'actions',
        header: () => null,
        cell: ({ row }) => {
          if (isRenderingPlotLabels || !row?.original?.project_id || !showActions) {
            return null;
          }

          return <TableActionCell row={row} confirmPlotDelete={confirmPlotDelete} currentPage={currentPage} />;
        },
      },
    ],
    [
      customCells,
      t,
      currentPage,
      isRenderingPlotLabels,
      asset_column_name,
      showActions,
      enableRowSelection,
      organisationId,
      organizationIdArg,
      resolvedPlotFilters,
      totalPlotCount,
      currentSelectedPlotIds,
      updateSelectedPlotIds,
    ],
  );

  const rowSelection = useMemo<RowSelectionState>(() => {
    if (!enableRowSelection || currentSelectedPlotIds.length === 0 || plots.length === 0) {
      return {};
    }

    const nextRowSelection: RowSelectionState = {};
    currentSelectedPlotIds.forEach((plotId) => {
      const rowIndex = plots.findIndex((plot) => plot.id === plotId);
      if (rowIndex !== -1) {
        nextRowSelection[rowIndex.toString()] = true;
      }
    });

    return nextRowSelection;
  }, [currentSelectedPlotIds, enableRowSelection, plots]);

  useEffectOnce(() => {
    if (defaultRowSelected && plots.length > 0) {
      const plotIds = plots.map((plot) => plot.id);
      if (currentSelectedPlotIds.length === 0 && JSON.stringify(plotIds) !== JSON.stringify(currentSelectedPlotIds)) {
        updateSelectedPlotIds(plotIds);
      }
    }
  }, [plots, defaultRowSelected, currentSelectedPlotIds, updateSelectedPlotIds]);

  const table = useReactTable({
    data: plots as unknown as PlotRow[],
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection,
    onRowSelectionChange: (updaterOrValue) => {
      const nextRowSelection =
        typeof updaterOrValue === 'function' ? updaterOrValue(rowSelection) : (updaterOrValue as RowSelectionState);

      const currentPagePlotIds = new Set(plots.map((plot) => plot.id));
      const previouslySelectedPlotIds = currentSelectedPlotIds.filter((id) => !currentPagePlotIds.has(id));

      const currentPageSelectedPlotIds = Object.entries(nextRowSelection)
        .filter(([, isSelected]) => Boolean(isSelected))
        .map(([index]) => plots[Number(index)]?.id)
        .filter((plotId): plotId is string => !isNullish(plotId));

      updateSelectedPlotIds([...previouslySelectedPlotIds, ...currentPageSelectedPlotIds]);
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize,
      },
    },
    state: {
      sorting,
      rowSelection,
    },
  });

  const patchPlotMutationStatus = useMutationState({
    filters: { mutationKey: [membershipType, 'plot'] },
    select: (mutation) => mutation.state.status,
  }).at(-1);

  const loading = patchPlotMutationStatus === 'pending' || plotsForOrganizationQuery.isRefetching;

  const rowsCountText = isRenderingPlotLabels
    ? `${totalPlotCount} ${plotLabel.name}`
    : t('shared.plots.pagination.titleMobile', {
        count: totalPlotCount,
      });

  const widgetConfiguration = { ...(showSort && sortWidgetConfiguration) } as WidgetSlotConfiguration;

  if (plots.length === 0) {
    return (
      <Stack className='justify-center items-center border border-neutral-black-12 rounded-2xl p-8'>
        {t('shared.plots.noPlots')}
      </Stack>
    );
  }

  return (
    <>
      <Stack spacing={8} data-testid='plot-list-desktop' className='w-full overflow-hidden scrollbar-hidden'>
        <Stack className='flex flex-row items-center justify-between gap-2'>
          <Stack direction='row' className='gap-4 items-center'>
            {showSort && <span className='typography-overline'>{rowsCountText}</span>}
            {!isLimitedAccessUser && isOrganizationMember && enableRowSelection && showGroupByOptions && (
              <Stack direction='row' className='gap-4 items-center'>
                <CreatePlotGroup
                  key='selected-group'
                  organizationId={organizationId}
                  disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected() && !defaultRowSelected}
                  plotIds={currentSelectedPlotIds}
                />
                {currentSelectedPlotIds.length > 0 && (
                  <span className='typography-overline'>
                    {t('global.organization.plotGroup.createPlotGroup.selectedRows', {
                      count: currentSelectedPlotIds.length,
                    })}
                  </span>
                )}
              </Stack>
            )}
          </Stack>
          <Stack direction='row' spacing={2} className='items-center'>
            <Stack className='flex h-11 flex-row gap-1 bg-neutral-black-4 border border-neutral-black-4 rounded-full justify-start px-3 py-1 items-center min-w-[150px]'>
              <Icon icon='ri-search-line' className='text-md' />
              <TextInput
                placeholder={t('shared.searchBar.placeholder')}
                className='w-full typography-body1 border-none bg-transparent h-full focus:border-none focus:outline-none focus-visible:outline-none px-0 py-0'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <Button
                  variant='text'
                  className='text-text-secondary hover:text-text-primary px-1 py-1'
                  onClick={() => setSearchQuery('')}
                >
                  <Icon icon='ri-close-line' className='text-sm' />
                </Button>
              )}
            </Stack>
            <WidgetSlotRenderer nestedWidget={widgetConfiguration} />
          </Stack>
        </Stack>
        <div className='relative bg-white-100 rounded-2xl border border-neutral-black-12'>
          {loading && (
            <Stack center className='absolute left-0 top-0 z-10 h-full w-full bg-neutral-white-50'>
              <Spinner size={10} />
            </Stack>
          )}
          <Table data-testid='plots-table'>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => {
                const renderPlotLabelView =
                  (isPlotsPage && isRenderingPlotLabels) || isNullish(row.original?.project_id);

                return renderPlotLabelView ? (
                  <TablePlotLabelRow
                    key={row.id}
                    row={row.original as PlotRow}
                    cells={row.getVisibleCells() as unknown as Cell<PlotRow, unknown>[]}
                    plotLabelValues={plotLabelValues}
                  />
                ) : (
                  <TablePlotRow
                    key={row.id}
                    plot={row.original as PlotRow}
                    cells={row.getVisibleCells() as unknown as Cell<PlotRow, unknown>[]}
                  />
                );
              })}
            </TableBody>
            {!isRenderingPlotLabels && (
              <TableFooter>
                <TableRow key='table-footer'>
                  <TableCell colSpan={columns.length}>
                    <Stack direction='row' className='items-center justify-start gap-3'>
                      <CheckboxCell
                        {...{
                          checked: table.getIsAllPageRowsSelected(),
                          indeterminate: table.getIsSomePageRowsSelected(),
                          onChange: table.getToggleAllPageRowsSelectedHandler(),
                        }}
                      />
                      <span className='typography-overline'>
                        {t('shared.plots.pagination.rowsInPage', { count: table.getRowModel().rows.length })}
                      </span>
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </div>
        {showPagination && (
          <Pagination
            className='typography-overline text-text-secondary mx-auto'
            currentPage={currentPage}
            totalCount={totalPlotCount}
            pageSize={pageSize}
            onPageChange={(nextPage) => {
              table.setPageIndex(nextPage - 1);
              setCurrentPage(nextPage);
            }}
            data-testid='pagination'
          />
        )}
      </Stack>

      <DeletePlotWithConfirmation plot={plotForDeletion} onClose={() => setPlotForDeletion(null)} />
    </>
  );
};

const TablePlotLabelRow = ({
  row,
  cells,
  plotLabelValues,
}: {
  row: PlotRow;
  cells: Cell<PlotRow, unknown>[];
  plotLabelValues: PlotLabelValue[] | null;
}) => {
  const { setSecondaryPlotGroupPlotLabel } = useSetDetailsPlotGroup();
  const pathSet = usePathSet();
  const navigate = useNavigate();
  const labelValue = plotLabelValues?.find((label) => label.value === row.id);

  const onRowClick = async () => {
    if (!labelValue) {
      return;
    }

    const { id } = await setSecondaryPlotGroupPlotLabel([labelValue]);

    const destinationPath = buildPath(pathSet.details, {
      queryParams: { plotGroupId: id },
    });

    navigate(destinationPath, {
      state: {
        previousPath: pathSet.plots,
        plotPage: '1',
      },
    });
  };

  return (
    <TableRow data-testid='plot-row' className='group/plot-row' onClick={onRowClick}>
      {cells.map((cell: Cell<PlotRow, unknown>) => {
        const cellMeta = (cell.column.columnDef.meta ?? {}) as { styles?: { cell?: string } };
        // Do not render succeeding columns since plot is invalid. This will change with the new onboarding journey, consider as temp fix
        if (
          ![
            ProjectTypeAccessorKey.name,
            ProjectTypeAccessorKey.type,
            ProjectTypeAccessorKey.area,
            'select',
            'actions',
          ].includes(cell.column.id as ProjectTypeAccessorKey) &&
          UNANALYZABLE_PLOT_STATUS_TYPES.includes(row.status)
        ) {
          return <TableCell key={cell.id} className={cellMeta.styles?.cell} />;
        }

        return (
          <TableCell key={cell.id} className={cellMeta.styles?.cell}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

const TablePlotRow = ({ plot, cells }: { plot: PlotRow; cells: Cell<PlotRow, unknown>[] }) => {
  const { getDestinationPath } = useDestinationPath();
  const destinationPath = getDestinationPath(plot);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  const onRowClick = () => {
    return (
      destinationPath &&
      navigate(destinationPath, {
        state: {
          previousPath: buildPath(pathname, {
            queryParams: {
              page: currentPage,
            },
          }),
          plotPage: `${currentPage}`,
        },
      })
    );
  };

  return (
    <TableRow data-testid='plot-row' onClick={onRowClick} className='group/plot-row hover:cursor-pointer'>
      {cells.map((cell: Cell<PlotRow, unknown>) => {
        const cellMeta = (cell.column.columnDef.meta ?? {}) as { styles?: { cell?: string } };
        // Do not render succeeding columns since plot is invalid. This will change with the new onboarding journey, consider as temp fix
        if (
          ![
            ProjectTypeAccessorKey.name,
            ProjectTypeAccessorKey.type,
            ProjectTypeAccessorKey.area,
            'select',
            'actions',
          ].includes(cell.column.id as ProjectTypeAccessorKey) &&
          UNANALYZABLE_PLOT_STATUS_TYPES.includes(plot.status)
        ) {
          return <TableCell key={cell.id} className={cellMeta.styles?.cell} />;
        }

        return (
          <TableCell key={cell.id} className={cellMeta.styles?.cell}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

const TableActionCell = ({
  row,
  confirmPlotDelete,
  currentPage,
}: {
  row: Row<PlotRow>;
  confirmPlotDelete: (plot: Plot) => void;
  currentPage: number;
}) => {
  const pathSet = usePathSet();
  const plot = row.original;
  const projectId = plot.project_id as string;
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const membershipType = useMembershipType();
  const isValid = plot.status !== PlotStatusEnum.invalid;

  // TODO: Uncomment when FE plot edit functionality is working
  /* const editLabel = useMemo(() => {
  *   const exhaustivenessCheck = (status: never) => {
  *     Logger.error(`No edit label set for plot status ${status}`);
  *     return t('shared.plots.overflowMenu.editData');
  *   };
  
  *   switch (plot.status) {
  *     case PlotStatusEnum.invalid:
  *       return t('shared.plots.overflowMenu.reviewData');
  *     case PlotStatusEnum.new_plot:
  *       return t('shared.plots.overflowMenu.addData');
  *     case PlotStatusEnum.draft:
  *       return t('shared.plots.overflowMenu.confirmData');
  *     case PlotStatusEnum.ready_to_analyse:
  *     case PlotStatusEnum.scheduled_for_analysis:
  *     case PlotStatusEnum.analysed:
  *     case PlotStatusEnum.failed:
  *       return t('shared.plots.overflowMenu.editData');
  *     default:
  *       return exhaustivenessCheck(plot.status);
  *   }
  * }, [plot.status, t]); */

  if (membershipType === MembershipWithOrganizationTypeEnum.land_steward) {
    return (
      <Dropdown modal={true}>
        <DropdownTrigger data-testid='plot-row-overflow-menu'>
          <div className='shrink-0 rounded-full border border-primary-100 p-2 text-primary-100 hover:bg-neutral-black-4'>
            <RiMore2Line size={24} />
          </div>
        </DropdownTrigger>

        {/* TODO: MVP-CW make dropdown options independent on the project id */}
        <DropdownContent side='bottom' sideOffset={10}>
          {isValid && (
            <DropdownItemLink
              disabled={!projectId}
              to={buildPath(paths.landSteward.plot, {
                pathParams: {
                  projectId,
                  plotId: plot.id,
                },
              })}
              state={{
                previousPath: buildPath(pathname, {
                  queryParams: {
                    page: currentPage,
                  },
                }),
                plotPage: `${currentPage}`,
              }}
              leftAdornment={<RiEyeLine />}
            >
              {t('shared.plots.overflowMenu.viewPlot')}
            </DropdownItemLink>
          )}
          {/* TODO: Uncomment when FE plot edit functionality is working */}
          {/* <EditPlotDropdownItemLink
            disabled={false}
            disabledHint={t('shared.projects.plot.tooltips.disabledEditOrDeletePlot')}
            to={buildPath(paths.landSteward.editPlot, {
              pathParams: {
                plotId: plot.id,
                projectId,
              },
            })}
            state={{
              previousPath: buildPath(pathname, {
                queryParams: {
                  page: currentPage,
                },
              }),
              plotPage: `${currentPage}`,
            }}
            leftAdornment={<RiEditLine />}
          >
            {editLabel}
          </EditPlotDropdownItemLink> */}
          <DeletePlotDropdownItem
            disabled={false}
            disabledHint={t('shared.projects.plot.tooltips.disabledEditOrDeletePlot')}
            onClick={() => confirmPlotDelete(plot)}
            leftAdornment={<RiDeleteBinLine />}
          >
            {t('shared.plots.overflowMenu.deletePlot')}
          </DeletePlotDropdownItem>
        </DropdownContent>
      </Dropdown>
    );
  }

  return isValid ? (
    <Link
      to={buildPath(pathSet.plot, {
        pathParams: { projectId, plotId: row.original.id },
      })}
      state={{
        previousPath: buildPath(pathname, {
          queryParams: {
            page: currentPage,
          },
        }),
        plotPage: `${currentPage}`,
      }}
    >
      <Stack center className='h-10 w-10 rounded-full border border-primary-100 hover:bg-neutral-black-4'>
        <RiArrowRightSLine size={24} />
      </Stack>
    </Link>
  ) : null;
};

// TODO: Uncomment when FE plot edit functionality is working
/* type EditPlotDropdownItemLinkProps = DropdownItemLinkProps & {
 *   disabledHint: string;
 * };
 *  */

// TODO: Uncomment when FE plot edit functionality is working
/* const EditPlotDropdownItemLink: FC<EditPlotDropdownItemLinkProps> = ({ children, disabledHint, to, ...delegated }) => {
 *   if (!delegated.disabled) {
 *     return (
 *       <DropdownItemLink to={to} {...delegated}>
 *         {children}
 *       </DropdownItemLink>
 *     );
 *   }
 *
 *   return (
 *     <Tooltip>
 *       <TooltipTrigger asChild>
 *         <DropdownItem {...delegated}>{children}</DropdownItem>
 *       </TooltipTrigger>
 *       <TooltipContent sideOffset={5} side='left'>
 *         {disabledHint}
 *         <TooltipArrow className='fill-white-100' />
 *       </TooltipContent>
 *     </Tooltip>
 *   );
 * };
 *  */
type DeletePlotDropdownItemProps = DropdownItemProps & {
  disabledHint: string;
};

const DeletePlotDropdownItem: FC<DeletePlotDropdownItemProps> = ({ children, disabledHint, ...delegated }) => {
  if (!delegated.disabled) {
    return <DropdownItem {...delegated}>{children}</DropdownItem>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <DropdownItem {...delegated}>{children}</DropdownItem>
      </TooltipTrigger>
      <TooltipContent sideOffset={5} side='left'>
        {disabledHint}
        <TooltipArrow className='fill-white-100' />
      </TooltipContent>
    </Tooltip>
  );
};
